import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, Outlet, useOutletContext } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useWorkspace } from '@/contexts/workspace-context';
import { useSession } from '@/contexts/session-context';
import { isValidPublicId } from '@/lib/public-id';
import type { WorkspacePublicId } from '@hookmyapp/shared';

/**
 * Phase 118 — WorkspaceLayout.
 *
 * Reads :ws from useParams, validates format + membership, then calls
 * switchWorkspace(ws) inside a useEffect. Renders <Outlet /> on valid,
 * silently redirects to `/` on invalid/stale (matches Phase 117
 * stale-UUID localStorage silent-drop pattern — no 403/404 page).
 *
 * Forwards the parent `<Outlet context>` down to children (DashboardPage
 * reads `onConnectClick` via useOutletContext — that context is set by
 * DashboardLayout above us in the route tree).
 *
 * NOTE: `switchWorkspace` fires in `useEffect` AFTER `<Outlet />` renders.
 * On a deep-link to /w/:ws/* (e.g. a shared link or bookmark), there is a
 * one-render window where `useWorkspace().activeWorkspace` still reflects
 * the previous workspace (or null) while the URL `:ws` segment is already
 * the new workspace. Phase 118 Meta OAuth correctness is guaranteed by the
 * `state` URL param (which carries `wsPublicId` as source of truth), NOT
 * by `activeWorkspace`. Any component that reads `activeWorkspace` on its
 * first render after a deep-link should be aware of this one-render lag
 * and fall back to `useParams().ws` if strict URL-identity matters.
 */
export function WorkspaceLayout() {
  const { ws } = useParams();
  const { workspaces, crossOrgWorkspaces, switchWorkspace, loading, refreshWorkspaces } =
    useWorkspace();
  const { organizationId, switchOrganization } = useSession();
  const parentContext = useOutletContext();
  const known = !!ws && workspaces.some((w) => w.id === ws);
  // A :ws missing from the list is often just a STALE list, not a foreign id —
  // e.g. a customer workspace created after the provider's initial fetch
  // (row click on Customers, or the post-connect redirect). Refetch once per
  // :ws before concluding it's invalid; only bounce to / if it's still absent.
  const [refetchedFor, setRefetchedFor] = useState<string | null>(null);

  // Sync URL ws → context (one-shot per url-change; switchWorkspace is stable useCallback).
  useEffect(() => {
    if (ws && isValidPublicId(ws, 'ws') && workspaces.some((w) => w.id === ws)) {
      switchWorkspace(ws as WorkspacePublicId);
    }
    // Intentionally omit switchWorkspace from deps — it is a stable useCallback
    // and including it would re-fire on every context update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, workspaces]);

  useEffect(() => {
    if (loading || known || !ws || !isValidPublicId(ws, 'ws') || refetchedFor === ws) return;
    void refreshWorkspaces().finally(() => setRefetchedFor(ws));
    // refreshWorkspaces is a stable useCallback from the provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, known, ws, refetchedFor]);

  // AIT-539 — a :ws the session org cannot see is not automatically a stale or
  // foreign id. `workspaces` is the session-org subset, so a workspace the user
  // legitimately holds in ANOTHER org reads as unknown here and used to bounce
  // silently to `/` — the same wrong verdict the Organization view made on a
  // cross-org 403. Check the cross-org union first: a hit means "yours, wrong
  // scope", so re-scope into its org and let the render below find it. One
  // attempt per :ws; a genuine miss still bounces.
  const rescopedForRef = useRef<string | null>(null);
  const foreignOrgWorkspace = useMemo(
    () =>
      !loading && !known && ws
        ? crossOrgWorkspaces.find(
            (w) => w.id === ws && w.organizationPublicId !== organizationId,
          ) ?? null
        : null,
    [loading, known, ws, crossOrgWorkspaces, organizationId],
  );
  const [rescopeFailedFor, setRescopeFailedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!foreignOrgWorkspace || !ws) return;
    if (rescopedForRef.current === ws) return;
    rescopedForRef.current = ws;
    void switchOrganization(foreignOrgWorkspace.organizationPublicId)
      // Release the guard on success: it prevents a re-entrant attempt while one
      // is in flight, not a legitimate second re-scope after another tab flips
      // the shared cookie and makes this workspace foreign again.
      .then(() => {
        rescopedForRef.current = null;
      })
      // A rejected re-scope must fall through to the normal bounce: the guard
      // stays set, so without this the view would hold its spinner forever with
      // neither an error nor a redirect.
      .catch(() => setRescopeFailedFor(ws));
  }, [foreignOrgWorkspace, ws, switchOrganization]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ws || !isValidPublicId(ws, 'ws')) {
    return <Navigate to="/" replace />;
  }

  if (!known) {
    if (foreignOrgWorkspace && rescopeFailedFor !== ws) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (refetchedFor !== ws) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }

  return <Outlet context={parentContext} />;
}
